import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "../AuthContext";

import {
  buscarNotificacoes,
  buscarQuantidadeNaoLidas,
  marcarNotificacaoComoLida,
  marcarTodasNotificacoesComoLidas,
  NotificationProps,
} from "shared/services/api/notificacoes.api";

export type { NotificationProps };

export interface NotificationContextData {
  notifications: NotificationProps[];
  unreadCount: number;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  carregarNotificacoes: () => Promise<void>;
  carregarMaisNotificacoes: () => Promise<void>;
  marcarComoLida: (id: string) => Promise<void>;
  marcarTodasComoLidas: () => Promise<void>;
  removerNotificacao: (id: string) => Promise<void>;
}

interface ProviderProps {
  children: React.ReactNode;
}

export const NotificacoesContext = createContext({} as NotificationContextData);

export function NotificacoesProvider({ children }: ProviderProps) {
  const { signed } = useAuth();

  const [notifications, setNotifications] = useState<NotificationProps[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const carregarNotificacoes = useCallback(async () => {
    if (!signed) {
      setNotifications([]);
      setUnreadCount(0);
      setPage(1);
      setHasMore(true);
      return;
    }

    try {
      setLoading(true);

      const [notificacoesResponse, quantidadeNaoLidas] = await Promise.all([
        buscarNotificacoes(1, 20),
        buscarQuantidadeNaoLidas(),
      ]);

      setNotifications(notificacoesResponse.data ?? []);
      setUnreadCount(quantidadeNaoLidas);
      setPage(1);
      setHasMore(notificacoesResponse.page < notificacoesResponse.lastPage);
    } catch (error) {
      console.error("Erro ao carregar notificações:", error);
    } finally {
      setLoading(false);
    }
  }, [signed]);

  const carregarMaisNotificacoes = useCallback(async () => {
    if (!signed || loading || loadingMore || !hasMore) return;

    try {
      setLoadingMore(true);

      const proximaPagina = page + 1;
      const response = await buscarNotificacoes(proximaPagina, 20);

      setNotifications((prev) => {
        const idsExistentes = new Set(prev.map((item) => item.id));
        const novasNotificacoes = (response.data ?? []).filter(
          (item) => !idsExistentes.has(item.id)
        );
        return [...prev, ...novasNotificacoes];
      });

      setPage(proximaPagina);
      setHasMore(response.page < response.lastPage);
    } catch (error) {
      console.error("Erro ao carregar mais notificações:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [signed, loading, loadingMore, hasMore, page]);

  const marcarComoLida = useCallback(async (id: string) => {
    const notificacao = notifications.find((item) => item.id === id);

    if (!notificacao || notificacao.read) {
      return;
    }

    await marcarNotificacaoComoLida(id);

    setNotifications((prev) =>
      prev.map((item) =>
        item.id === id
          ? {
              ...item,
              read: true,
            }
          : item
      )
    );

    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, [notifications]);

  const marcarTodasComoLidas = useCallback(async () => {
    if (unreadCount === 0) {
      return;
    }

    await marcarTodasNotificacoesComoLidas();

    setNotifications((prev) =>
      prev.map((item) => ({
        ...item,
        read: true,
      }))
    );

    setUnreadCount(0);
  }, [unreadCount]);

  const removerNotificacao = useCallback(async (id: string) => {
    setNotifications((prev) => prev.filter((item) => item.id !== id));
  }, []);

  useEffect(() => {
    if (!signed) {
      setNotifications([]);
      setUnreadCount(0);
      setPage(1);
      setHasMore(true);
      return;
    }

    carregarNotificacoes();

    const intervalo = setInterval(() => {
      carregarNotificacoes();
    }, 5000);

    return () => {
      clearInterval(intervalo);
    };
  }, [signed, carregarNotificacoes]);

  const value = useMemo(
    () => ({
      notifications,
      unreadCount,
      loading,
      loadingMore,
      hasMore,
      carregarNotificacoes,
      carregarMaisNotificacoes,
      marcarComoLida,
      marcarTodasComoLidas,
      removerNotificacao,
    }),
    [
      notifications,
      unreadCount,
      loading,
      loadingMore,
      hasMore,
      carregarNotificacoes,
      carregarMaisNotificacoes,
      marcarComoLida,
      marcarTodasComoLidas,
      removerNotificacao,
    ]
  );

  return (
    <NotificacoesContext.Provider value={value}>
      {children}
    </NotificacoesContext.Provider>
  );
}